import {Component, inject} from '@angular/core';
import {NgForOf} from '@angular/common';
import {displayShortDate} from '@ui-kit/utils/static/date-format-utils';
import {
  RetailerTransactionStatementsModel
} from '@app/pages/admin-panel/retailer-transaction-statement/retailer-transaction-statements.model';
import {TransactionStatementComponent} from '@features/transactions/statements/transaction-statement.component';
import {JsonFormsAngularMaterialModule} from '@jsonforms/angular-material';
import {DateRange} from '@angular/material/datepicker';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {CustomDateRangeModel, TimeFrames} from '@ui-webkit/components/custom-date-filter/custom-date-range.model';
import {
  CustomDateRangeChangeEventModel
} from '@ui-webkit/components/custom-date-filter/custom-date-range-change-event.model';
import {CustomDateFilterComponent} from '@ui-webkit/components/custom-date-filter/custom-date-filter.component';
import {
  RetailerTransactionStatementsService
} from '@app/pages/admin-panel/retailer-transaction-statement/retailer-transaction-statements.service';
import {MatProgressBar} from '@angular/material/progress-bar';
import {PageHeaderComponent} from '@ui-webkit/components/page-header/page-header.component';
import {LoadingService} from '@ui-kit/utils/service/loading.service';
import * as XLSX from 'xlsx';
import {FlexModule} from '@angular/flex-layout';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatTooltip} from '@angular/material/tooltip';
import {SelectAllDirective} from '@ui-kit/framework/directive/select-all.directive';

declare let html2pdf: any;


@Component({
  standalone: true,
  imports: [
    NgForOf,
    TransactionStatementComponent,
    JsonFormsAngularMaterialModule,
    ReactiveFormsModule,
    CustomDateFilterComponent,
    MatProgressBar,
    PageHeaderComponent,
    FlexModule,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    MatMenuTrigger,
    SelectAllDirective
  ],
  templateUrl: './retailer-transaction-statements.component.html',
  styleUrl: './retailer-transaction-statements.component.scss'
})
export class RetailerTransactionStatementsComponent {

  dateIssued: string = displayShortDate(new Date())!;
  currency: string = 'ZAR';
  summaries: RetailerTransactionStatementsModel[] = [];
  dateRange: DateRange<Date | null> = new DateRange<Date | null>(null, null);
  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('this_month', 0, TimeFrames.MONTH, 'This month'),
    new CustomDateRangeModel('last_month', 1, TimeFrames.MONTH, 'Last Month'),
  ];
  exportRetailerOptions: string[] = [];
  retailersCtrl : FormControl = new FormControl<string[] | undefined>([]);
  private _loader = inject(LoadingService);
  protected loading$ = this._loader.loading$;
  private retailerTransactionSummaryService = inject(RetailerTransactionStatementsService);

  closedSalesColumns = [
    {displayLabel: 'Trading Date', colName: 'tradingDate', mappingFunction: displayShortDate},
    {displayLabel: 'Total Sales', colName: 'totalSales', mappingFunction: null},
    {displayLabel: 'Platform Payments', colName: 'platformPayments', mappingFunction: null},
    {displayLabel: 'External Payments', colName: 'externalPayments', mappingFunction: null},
    {displayLabel: 'Charges (Ex. VAT)', colName: 'charges', mappingFunction: null},
    {displayLabel: 'VAT on Charges', colName: 'vatOnCharges', mappingFunction: null},
    {displayLabel: 'Settlement Balance', colName: 'settlementBalance', mappingFunction: null},
  ]

  payoutSummaryColumns = [
    {displayLabel: 'Payout Date', colName: 'processedDate', mappingFunction: displayShortDate},
    {displayLabel: 'Earliest Transaction Date', colName: 'earliestTransactionDate', mappingFunction: displayShortDate},
    {displayLabel: 'Latest Transaction Date', colName: 'latestTransactionDate', mappingFunction: displayShortDate},
    {displayLabel: 'Total Sales', colName: 'totalSales', mappingFunction: null},
    {displayLabel: 'Charges (Inc. VAT)', colName: 'chargesVatIncl', mappingFunction: null},
    {displayLabel: 'Settlement Hold', colName: 'settlementHold', mappingFunction: null},
    {displayLabel: 'Settlement Balance', colName: 'tenantSettlement', mappingFunction: null},
    {displayLabel: 'Total Paid', colName: 'totalPaid', mappingFunction: null},
  ]

  constructor() {
  }


  downloadAsExcel(): void {
    let filteredRetailers: RetailerTransactionStatementsModel[] = this.summaries.filter(fl => {
      return (this.retailersCtrl.value && this.retailersCtrl.value.length != 0) ? this.retailersCtrl.value.includes(fl.retailerCompanyName) : [];
    });
    for (let statement of filteredRetailers) {
      this.exportToExcel(statement);
    }
  }

  exportToExcel(statement: RetailerTransactionStatementsModel) {

    let closedSalesData = statement.closedSales.map((item: any) => {
      const newItem: any = {};
      this.closedSalesColumns.forEach(column => {
        newItem[column.displayLabel] = column.mappingFunction != null ? column.mappingFunction(item[column.colName]) : item[column.colName];
      });
      return newItem;
    });

    closedSalesData.push({
      'Trading Date': 'Totals',
      'Total Sales': statement.closedSalesTotals.totalSales,
      'Platform Payments': statement.closedSalesTotals.platformPayments,
      'External Payments': statement.closedSalesTotals.externalPayments,
      'Charges (Ex. VAT)': statement.closedSalesTotals.charges,
      'VAT on Charges': statement.closedSalesTotals.vatOnCharges,
      'Settlement Balance': statement.closedSalesTotals.settlementBalance,
    });

    let payoutData = statement.payoutSummary.map((item: any) => {
      const newItem: any = {};
      this.payoutSummaryColumns.forEach(column => {
        newItem[column.displayLabel] = column.mappingFunction != null ? column.mappingFunction(item[column.colName]) : item[column.colName];
      });
      return newItem;
    });

    payoutData.push({
      'Payout Date': 'Totals',
      'Earliest Transaction Date': '',
      'Latest Transaction Date': '',
      'Total Sales': statement.payoutTotals.totalSales,
      'Charges (Inc. VAT)': statement.payoutTotals.chargesVatIncl,
      'Settlement Hold': statement.payoutTotals.settlementHold,
      'Settlement Balance': statement.payoutTotals.tenantSettlement,
      'Total Paid': statement.payoutTotals.totalPaid
    });

    const granddTotalsData: any[] = [{
      'Total Sales': statement.grandTotals.totalSales,
      'Total Charges (Vat Incl)': statement.grandTotals.totalChargesIncl,
      'Total External Payments': statement.grandTotals.totalExternalPayments,
      'Total Settlement Holds': statement.grandTotals.totalSettlementHolds,
      'Total Settled': statement.grandTotals.totalSettled,
      'Settlement Balance For Period': statement.grandTotals.settlementBalanceForPeriod,
    }]

    const closedSalesWs: XLSX.WorkSheet = XLSX.utils.json_to_sheet(closedSalesData);
    const payoutWs: XLSX.WorkSheet = XLSX.utils.json_to_sheet(payoutData);
    const grandTotalWs: XLSX.WorkSheet = XLSX.utils.json_to_sheet(granddTotalsData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, closedSalesWs, 'Closed Sales');
    XLSX.utils.book_append_sheet(wb, payoutWs, 'Payout');
    XLSX.utils.book_append_sheet(wb, grandTotalWs, 'Grand Totals');

    XLSX.writeFile(wb, `${statement.retailerCompanyName}-${new Date().toISOString()}.xlsx`);
  }

  downloadAsPdf(): void {
    const pages = document.querySelectorAll('.transaction-summary-invoice');
    const pagesArray = Array.from(pages);
    let filteredRetailers: Element[] = pagesArray.filter(fl => {
      return (this.retailersCtrl.value && this.retailersCtrl.value.length != 0) ? this.retailersCtrl.value.includes(fl.id) : [];
    });
    for (let page of filteredRetailers) {
      this.exportToPdf(page as HTMLElement)
    }
  }


  async exportToPdf(retailerStatementElement: HTMLElement) {
    let individualPages = Array.from(retailerStatementElement.querySelectorAll('.a4-paper'));

    const options = {
      margin: 0,
      filename: `Transaction-summary-${retailerStatementElement.id}-${new Date().toISOString()}.pdf`,
      image: {type: 'jpeg', quality: '0.98'},
      html2canvas: {scale: 2},
      jsPDF: {unit: 'mm', format: 'a4', orientation: 'portrait'}
    }
    const pdfChain = html2pdf().set(options);

    // Iterate over all elements and add each to the PDF
    individualPages.reduce((chain, element, index) => {
      // @ts-ignore
      return chain.from(element).toContainer().toCanvas().toPdf().get('pdf').then(pdf => {
      });
    }, pdfChain)
      .save(); // Save the PDF after all elements are processed
  }

  loadStatements(): void {
    const filters = [
      {name: 'fromDate', val: displayShortDate(this.dateRange.start)},
      {name: 'toDate', val: this.dateRange.end ? displayShortDate(this.dateRange.end) : null},
    ];
    this.retailerTransactionSummaryService.loadStatements(filters).subscribe({
      next: (value: RetailerTransactionStatementsModel[]) => {
        this.summaries = value;
        this.summaries.sort((a, b) => a.retailerCompanyName.localeCompare(b.retailerCompanyName));
        this.exportRetailerOptions = this.summaries.map((item) => {
          return item.retailerCompanyName
        });
      }
    });
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    if (event.dateRange.start && event.dateRange.end) {
      this.dateRange = event.dateRange;
      this.loadStatements();
    }
  }
}
